import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../hooks/useAuth";
import { apiClient } from "../services/api";
import { FiFile, FiTrash2, FiDownload, FiZap } from "react-icons/fi";
import FormDeletionProgressModal from "./FormDeletionProgressModal";
import FormDeleteConfirmModal from "./FormDeleteConfirmModal";

interface FormTemplate {
  id: string;
  name: string;
  description: string;
  form_type: string;
  country: string;
  autofill_supported?: boolean;
}

interface UserForm {
  id: string;
  form_name: string;
  status:
    | "draft"
    | "filled"
    | "reviewing"
    | "ready"
    | "completed"
    | "not_started"
    | "queued"
    | "processing"
    | "failed";
  auto_fill_status?:
    | "not_started"
    | "queued"
    | "processing"
    | "filled"
    | "failed";
  filled_at?: string;
  reviewed_at?: string;
  review_notes?: string;
  created_at: string;
}

interface Case {
  id: string;
  title: string;
  country: string;
  caseType: string;
  caseTypeId: string;
}

export default function CaseForms() {
  const { user } = useAuth();
  const [selectedCase, setSelectedCase] = useState<string | null>(null);
  const [cases, setCases] = useState<any[]>([]);
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [recommendedForms, setRecommendedForms] = useState<FormTemplate[]>([]);
  const [userForms, setUserForms] = useState<UserForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [autoFilling, setAutoFilling] = useState<string | null>(null);
  const [addedFormIds, setAddedFormIds] = useState<string[]>([]);
  const [showCustomFormSearch, setShowCustomFormSearch] = useState(false);
  const [customFormSearch, setCustomFormSearch] = useState("");
  const [customFormResults, setCustomFormResults] = useState<FormTemplate[]>(
    []
  );
  const [deletingForm, setDeletingForm] = useState<{
    id: string;
    name: string;
    status: string;
  } | null>(null);

  // Bulk operation states
  const [selectedFormsForDelete, setSelectedFormsForDelete] = useState<
    string[]
  >([]);
  const [selectedFormsForDownload, setSelectedFormsForDownload] = useState<
    string[]
  >([]);
  const [selectedFormsForAutoFill, setSelectedFormsForAutoFill] = useState<
    string[]
  >([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkAutoFilling, setBulkAutoFilling] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);

  // Form Deletion Progress Modal State
  const [formDeletionProgress, setFormDeletionProgress] = useState<{
    isOpen: boolean;
    isDeleting: boolean;
    totalForms: number;
    deletedCount: number;
    currentFormName?: string;
  }>({
    isOpen: false,
    isDeleting: false,
    totalForms: 0,
    deletedCount: 0,
    currentFormName: undefined,
  });

  // Auto-fill polling state
  const [pollingForms, setPollingForms] = useState<Set<string>>(new Set());
  const [recentlyUpdatedForms, setRecentlyUpdatedForms] = useState<Set<string>>(
    new Set()
  );

  // Download progress state
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [selectingFolder, setSelectingFolder] = useState<boolean>(false);

  // Confirmation dialog state
  const [showFolderConfirm, setShowFolderConfirm] = useState<boolean>(false);
  const [selectedDirectory, setSelectedDirectory] = useState<any>(null);
  const [pendingDownloadCount, setPendingDownloadCount] = useState<number>(0);

  // Tooltip state for Download All button
  const [showDownloadTooltip, setShowDownloadTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setShowDownloadTooltip(false);
      }
    };

    if (showDownloadTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDownloadTooltip]);

  // Helper functions for polling control
  const startPolling = (formIds: string[]) => {
    console.log("🚀 Starting polling for forms:", formIds);
    setPollingForms(new Set(formIds));
  };

  const stopPolling = () => {
    console.log("🛑 Stopping all polling");
    setPollingForms(new Set());
  };

  const isPolling = pollingForms.size > 0;

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    if (selectedCase) {
      loadCaseData(selectedCase);
      loadUserForms(selectedCase);
    }
  }, [selectedCase]);

  // Auto-start polling for forms that are already processing when component loads
  useEffect(() => {
    if (userForms.length > 0 && pollingForms.size === 0) {
      const processingForms = userForms.filter(
        (form) =>
          form.status === "queued" ||
          form.status === "processing" ||
          form.auto_fill_status === "queued" ||
          form.auto_fill_status === "processing"
      );

      if (processingForms.length > 0) {
        console.log(
          "🚀 Auto-starting polling for processing forms:",
          processingForms.map((f) => f.form_name)
        );
        startPolling(processingForms.map((form) => form.id));
      }
    }
  }, [userForms, pollingForms.size]);

  // Auto-fill polling effect - Clean version with proper cleanup
  useEffect(() => {
    // Only start polling if we have forms to monitor
    if (pollingForms.size === 0) {
      return;
    }

    console.log("🔄 Starting polling for forms:", Array.from(pollingForms));

    // Create a single interval
    const interval = setInterval(async () => {
      if (!selectedCase) {
        return;
      }

      try {
        console.log("📊 Polling for status updates...");

        // Fetch fresh data
        const response = await apiClient.getUserForms(selectedCase, true);

        // Check for status changes before updating
        const changedForms = new Set<string>();
        response.forEach((newForm: any) => {
          const oldForm = userForms.find((f) => f.id === newForm.id);
          if (
            oldForm &&
            (oldForm.status !== newForm.status ||
              oldForm.auto_fill_status !== newForm.auto_fill_status)
          ) {
            changedForms.add(newForm.id);
            console.log(
              `📊 Status changed for ${newForm.form_name}: ${oldForm.status}/${oldForm.auto_fill_status} → ${newForm.status}/${newForm.auto_fill_status}`
            );
          }
        });

        // Update the UI with fresh data immediately
        setUserForms(response);
        console.log("✅ UI updated with fresh form data");

        // Remove forms from delete selection if they can no longer be deleted
        setSelectedFormsForDelete((prevSelected) =>
          prevSelected.filter((formId) => {
            const form = response.find((f: any) => f.id === formId);
            return form && canFormBeDeleted(form);
          })
        );

        // Highlight recently changed forms
        if (changedForms.size > 0) {
          setRecentlyUpdatedForms(changedForms);
          // Clear highlights after 3 seconds
          setTimeout(() => {
            setRecentlyUpdatedForms(new Set());
          }, 3000);
        }

        // Check which forms are still processing
        const stillProcessing = response.filter(
          (form: any) =>
            pollingForms.has(form.id) &&
            (form.status === "queued" ||
              form.status === "processing" ||
              form.auto_fill_status === "queued" ||
              form.auto_fill_status === "processing")
        );

        console.log(
          "📊 Forms still processing:",
          stillProcessing.length,
          stillProcessing.map((f: any) => f.form_name)
        );

        // If no forms are processing, stop polling
        if (stillProcessing.length === 0) {
          console.log("✅ All forms completed, stopping polling");
          setPollingForms(new Set());
        }
      } catch (error) {
        console.error("❌ Polling error:", error);
        // Stop polling on error to prevent infinite retries
        setPollingForms(new Set());
      }
    }, 5000); // 5 seconds for better user experience during autofill

    // Cleanup function - this is crucial!
    return () => {
      console.log("🧹 Cleaning up polling interval");
      clearInterval(interval);
    };
  }, [pollingForms, selectedCase, userForms]); // Include userForms to track status changes

  // Cleanup effect - stop polling when component unmounts or case changes
  useEffect(() => {
    return () => {
      if (pollingForms.size > 0) {
        console.log("🧹 Component unmounting, stopping all polling");
        setPollingForms(new Set());
      }
    };
  }, [pollingForms]);

  // Debug effect to log form autofill support
  useEffect(() => {
    if (userForms.length > 0) {
      console.log("🔍 Debug: Form autofill support status:");
      userForms.forEach((form) => {
        const isSupported = isFormAutofillSupported(form.form_name);
        const shouldShowAutofill =
          (form.status === "not_started" || form.status === "draft") &&
          isSupported;
        const shouldShowDownloadOnly = !isSupported; // Now shows regardless of status
        const displayStatus = getDisplayStatus(form);

        console.log(
          `  ${form.form_name}: status=${form.status}, displayStatus=${displayStatus}, supported=${isSupported}, showAutofill=${shouldShowAutofill}, showDownloadOnly=${shouldShowDownloadOnly}`
        );
      });
    }
  }, [userForms]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getCases();
      setCases(data);
      if (data.length > 0 && !selectedCase) {
        setSelectedCase(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCaseData = async (caseIdParam: string) => {
    try {
      console.log("🔍 Loading case data for:", caseIdParam);

      // Find the selected case from the cases list
      const selectedCaseData = cases.find((c) => c.id === caseIdParam);
      if (!selectedCaseData) {
        console.error("Case not found:", caseIdParam);
        return;
      }

      console.log("📋 Selected case data:", selectedCaseData);

      const caseData = {
        id: caseIdParam,
        title: selectedCaseData.title || "Immigration Case",
        country: selectedCaseData.country || "USA",
        caseType: selectedCaseData.caseType || "Other",
        caseTypeId: selectedCaseData.caseTypeId,
      };
      setCaseData(caseData);

      console.log("🔍 Loading forms for:", {
        caseTypeId: caseData.caseTypeId,
        country: caseData.country,
        caseType: caseData.caseType,
      });

      // Load recommended forms based on case type and country
      console.log("🔍 Loading recommended forms with:", {
        caseTypeId: caseData.caseTypeId,
        country: caseData.country,
        caseType: caseData.caseType,
      });

      const formsResponse = await apiClient.getRecommendedForms(
        caseData.caseTypeId,
        caseData.country,
        true // Force refresh to bypass cache
      );

      console.log("📋 Forms response:", formsResponse);
      setRecommendedForms(formsResponse);
    } catch (error) {
      console.error("Error loading case data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserForms = async (
    caseIdParam: string,
    forceRefresh: boolean = false
  ) => {
    try {
      console.log(
        "🔄 Loading user forms for case:",
        caseIdParam,
        forceRefresh ? "(force refresh)" : ""
      );
      const response = await apiClient.getUserForms(caseIdParam, forceRefresh);
      console.log("✅ Loaded user forms:", response);
      setUserForms(response);
      // Track added form template IDs
      const addedIds = response
        .map(
          (form: any) =>
            form.form_id || form.template_id || form.form_template_id
        )
        .filter(Boolean);
      console.log("📋 Added form IDs:", addedIds);
      setAddedFormIds(addedIds);
    } catch (error) {
      console.error("❌ Error loading user forms:", error);
    }
  };

  const handleFormSelection = (formId: string) => {
    setSelectedForms((prev) =>
      prev.includes(formId)
        ? prev.filter((id) => id !== formId)
        : [...prev, formId]
    );
  };

  const handleAddForms = async () => {
    if (!selectedCase) return;

    console.log("🔄 Starting to add forms:", { selectedCase, selectedForms });

    // Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.error(
        "⏰ Timeout: Form addition taking too long, forcing loading to false"
      );
      setLoading(false);
    }, 30000); // 30 second timeout

    try {
      setLoading(true);

      // Create user forms for selected templates
      for (const templateId of selectedForms) {
        console.log("📝 Creating user form for template:", templateId);
        const result = await apiClient.createUserForm(selectedCase, templateId);
        console.log("✅ Created user form:", result);
      }

      console.log("🔄 Reloading user forms...");
      // Reload user forms
      await loadUserForms(selectedCase);
      setSelectedForms([]);
      console.log("✅ Forms added successfully");
    } catch (error) {
      console.error("❌ Error adding forms:", error);
      alert(
        `Failed to add forms: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      clearTimeout(timeoutId);
      console.log("🏁 Setting loading to false");
      setLoading(false);
    }
  };

  const handleAutoFill = async (userFormId: string) => {
    try {
      setAutoFilling(userFormId);

      // Start polling for this specific form
      startPolling([userFormId]);

      await apiClient.autoFillForm(userFormId);

      console.log("✅ Autofill initiated, polling started");
    } catch (error) {
      console.error("Error auto-filling form:", error);
      // Stop polling on error
      stopPolling();
    } finally {
      setAutoFilling(null);
    }
  };

  // Enhanced download function with "Save As" dialog for modern browsers
  // REMOVED - Individual downloads no longer used in Selected Forms section
  // Users now use "Download All" or bulk download functionality

  const handleDeleteForm = async (
    userFormId: string,
    formName: string,
    formStatus: string
  ) => {
    // If status is draft, delete immediately without confirmation
    if (formStatus === "draft") {
      try {
        await apiClient.deleteUserForm(userFormId);
        if (selectedCase) {
          await loadUserForms(selectedCase, true); // Force refresh
        }
      } catch (error) {
        console.error("Error deleting form:", error);
        alert("Failed to delete form. Please try again.");
      }
      return;
    }

    // For non-draft forms, show confirmation dialog
    setDeletingForm({ id: userFormId, name: formName, status: formStatus });
  };

  const confirmDeleteForm = async () => {
    if (!deletingForm) return;

    try {
      await apiClient.deleteUserForm(deletingForm.id);
      if (selectedCase) {
        await loadUserForms(selectedCase, true); // Force refresh
      }
    } catch (error) {
      console.error("Error deleting form:", error);
      alert("Failed to delete form. Please try again.");
    } finally {
      setDeletingForm(null);
    }
  };

  const cancelDeleteForm = () => {
    setDeletingForm(null);
  };

  // Bulk operation handlers
  const handleBulkFormSelection = (
    formId: string,
    operation: "delete" | "download" | "autofill"
  ) => {
    switch (operation) {
      case "delete":
        // Only allow deletion if the form can be deleted
        const formToDelete = userForms.find((f) => f.id === formId);
        if (formToDelete && canFormBeDeleted(formToDelete)) {
          setSelectedFormsForDelete((prev) =>
            prev.includes(formId)
              ? prev.filter((id) => id !== formId)
              : [...prev, formId]
          );
        }
        break;
      case "download":
        // Allow download selection for all forms
        setSelectedFormsForDownload((prev) =>
          prev.includes(formId)
            ? prev.filter((id) => id !== formId)
            : [...prev, formId]
        );
        break;
      case "autofill":
        setSelectedFormsForAutoFill((prev) =>
          prev.includes(formId)
            ? prev.filter((id) => id !== formId)
            : [...prev, formId]
        );
        break;
    }
  };

  const handleBulkDelete = () => {
    if (selectedFormsForDelete.length === 0) return;
    setBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    if (!selectedCase) return;

    try {
      setBulkDeleting(true);
      setBulkDeleteModal(false);

      // Initialize form deletion progress modal
      const totalToDelete = selectedFormsForDelete.length;
      setFormDeletionProgress({
        isOpen: true,
        isDeleting: true,
        totalForms: totalToDelete,
        deletedCount: 0,
        currentFormName: undefined,
      });

      let deletedCount = 0;
      const errors: string[] = [];

      // Delete all selected forms with progress updates
      for (const formId of selectedFormsForDelete) {
        try {
          const form = userForms.find((f) => f.id === formId);
          const formName = form?.form_name || formId;

          // Update progress with current form
          setFormDeletionProgress((prev) => ({
            ...prev,
            currentFormName: formName,
          }));

          await apiClient.deleteUserForm(formId);
          deletedCount++;

          // Update progress count
          setFormDeletionProgress((prev) => ({
            ...prev,
            deletedCount: deletedCount,
          }));
        } catch (error) {
          const form = userForms.find((f) => f.id === formId);
          const formName = form?.form_name || formId;
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";
          errors.push(`Failed to delete "${formName}": ${errorMessage}`);
          console.error(`❌ Error deleting form ${formName}:`, error);
        }
      }

      // Mark deletion as complete
      setFormDeletionProgress((prev) => ({
        ...prev,
        isDeleting: false,
        currentFormName: undefined,
      }));

      // Clear selection and reload forms
      setSelectedFormsForDelete([]);
      await loadUserForms(selectedCase, true); // Force refresh

      console.log("✅ Bulk delete completed:", deletedCount, "forms");

      // If there were errors, show them after a delay
      if (errors.length > 0) {
        setTimeout(() => {
          alert(`Some forms failed to delete:\n${errors.join("\n")}`);
        }, 2000);
      }
    } catch (error) {
      console.error("Error during bulk delete:", error);

      // Mark deletion as complete with error
      setFormDeletionProgress((prev) => ({
        ...prev,
        isDeleting: false,
        currentFormName: undefined,
      }));

      // Show error message after a delay
      setTimeout(() => {
        alert("Failed to delete forms. Please try again.");
      }, 1000);
    } finally {
      setBulkDeleting(false);
    }
  };

  const cancelBulkDelete = () => {
    setBulkDeleteModal(false);
  };

  const handleBulkDownload = async () => {
    if (selectedFormsForDownload.length === 0) return;

    // Debug logging to see what's selected
    console.log("🔍 Debug: Bulk download initiated:", {
      selectedFormsForDownload: selectedFormsForDownload,
      selectedFormsCount: selectedFormsForDownload.length,
      userForms: userForms.map((f) => ({ id: f.id, name: f.form_name })),
    });

    try {
      setSelectingFolder(true);
      const directory = await (window as any).showDirectoryPicker({
        mode: "readwrite",
      });

      // Store the directory and show confirmation dialog
      setSelectedDirectory(directory);
      setPendingDownloadCount(selectedFormsForDownload.length);
      setShowFolderConfirm(true);
      setSelectingFolder(false);
      setBulkDownloading(false);
      setDownloadProgress(0);

      console.log("🔍 Debug: Folder selected, confirmation dialog shown:", {
        directoryName: directory.name,
        pendingDownloadCount: selectedFormsForDownload.length,
      });

      return; // Exit early, wait for user confirmation
    } catch (folderError) {
      console.log("Folder picker failed or was cancelled:", folderError);
      // User cancelled or folder picker failed - don't download anything
      setSelectingFolder(false);
      setBulkDownloading(false);
      setDownloadProgress(0);
      return; // Exit without downloading anything
    }
  };

  // Handle bulk auto-fill with proper polling control
  const handleBulkAutoFill = async () => {
    if (!selectedFormsForAutoFill.length) return;

    try {
      setBulkAutoFilling(true);

      // Start polling for all selected forms
      startPolling(selectedFormsForAutoFill);

      // Process each form
      for (const formId of selectedFormsForAutoFill) {
        try {
          await apiClient.autoFillForm(formId);
          console.log(`✅ Autofill initiated for form: ${formId}`);
        } catch (error) {
          console.error(`❌ Error autofilling form ${formId}:`, error);
        }
      }

      console.log("✅ Bulk autofill initiated, polling started");
    } catch (error) {
      console.error("❌ Error in bulk autofill:", error);
      stopPolling();
    } finally {
      setBulkAutoFilling(false);
      setSelectedFormsForAutoFill([]);
    }
  };

  const handleCustomFormSearch = async () => {
    try {
      if (!customFormSearch.trim()) {
        setCustomFormResults([]);
        return;
      }

      // Search for forms by name or description
      const results = await apiClient.searchCustomForms(customFormSearch);
      setCustomFormResults(results);
    } catch (error) {
      console.error("Error searching custom forms:", error);
      setCustomFormResults([]);
    }
  };

  // Enhanced bulk download with folder selection for modern browsers
  const handleDownloadAll = async () => {
    if (userForms.length === 0) return;

    try {
      setDownloadProgress(0);
      setBulkDownloading(true);

      const formsToDownload = userForms; // Download all forms
      let completed = 0;
      let failed = 0;

      // Try to use File System Access API for folder selection (modern browsers)
      let selectedDirectory: any = null;
      if ("showDirectoryPicker" in window) {
        try {
          setSelectingFolder(true);
          const directory = await (window as any).showDirectoryPicker({
            mode: "readwrite",
          });

          // Store the directory and show confirmation dialog
          setSelectedDirectory(directory);
          setPendingDownloadCount(formsToDownload.length);
          setShowFolderConfirm(true);
          setSelectingFolder(false);
          setBulkDownloading(false);
          setDownloadProgress(0);

          console.log("🔍 Debug: Download All - folder selected:", {
            directoryName: directory.name,
            pendingDownloadCount: formsToDownload.length,
            userFormsCount: userForms.length,
            isDownloadAll: true,
          });

          return; // Exit early, wait for user confirmation
        } catch (folderError) {
          console.log("Folder picker failed or was cancelled:", folderError);
          // User cancelled or folder picker failed - don't download anything
          setSelectingFolder(false);
          setBulkDownloading(false);
          setDownloadProgress(0);
          return; // Exit without downloading anything
        }
      }

      // If we reach here, it means no folder picker support or user cancelled
      // For older browsers, we can still download to default folder
      // But for modern browsers that support folder picker, we should not fall back
      if ("showDirectoryPicker" in window) {
        // Modern browser but no folder selected - user must have cancelled
        console.log(
          "Modern browser detected but no folder selected - user cancelled folder picker"
        );
        return; // Don't download anything
      }

      // Only fall back to default downloads for older browsers that don't support folder picker
      console.log(
        "Older browser detected - falling back to default download folder"
      );

      for (let index = 0; index < formsToDownload.length; index++) {
        const form = formsToDownload[index];

        try {
          // Update progress
          setDownloadProgress(
            Math.round((index / formsToDownload.length) * 100)
          );

          const response = await apiClient.downloadForm(form.id);

          if (response.pdfUrl) {
            // Create enhanced filename
            const timestamp = new Date().toISOString().slice(0, 10);
            const caseInfo = cases.find((c) => c.id === selectedCase);
            const clientName = caseInfo?.clientName || "client";
            const cleanClientName = clientName.replace(/[^a-zA-Z0-9]/g, "_");
            const enhancedFileName = `${cleanClientName}_${form.form_name.replace(
              ".pdf",
              ""
            )}_${timestamp}.pdf`;

            if (selectedDirectory) {
              // Save to selected folder using File System Access API
              try {
                const fileHandle = await selectedDirectory.getFileHandle(
                  enhancedFileName,
                  { create: true }
                );
                const writable = await fileHandle.createWritable();

                // Fetch the file content
                const fileResponse = await fetch(response.pdfUrl);
                const blob = await fileResponse.blob();

                // Write to selected folder
                await writable.write(blob);
                await writable.close();

                completed++;
                console.log(`✅ Saved ${enhancedFileName} to selected folder`);
              } catch (writeError) {
                console.error(
                  `Error writing ${enhancedFileName} to selected folder:`,
                  writeError
                );
                failed++;
              }
            } else {
              // Fallback: Regular download to default folder
              const fileResponse = await fetch(response.pdfUrl);
              const blob = await fileResponse.blob();
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = enhancedFileName;
              link.style.display = "none";

              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              completed++;
            }

            // Small delay between downloads to prevent browser overload
            if (index < formsToDownload.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          }
        } catch (error) {
          console.error(`Error downloading ${form.form_name}:`, error);
          failed++;
          // Continue with other forms
        }
      }

      // Final progress update
      setDownloadProgress(100);

      // Show completion message based on method used
      setTimeout(() => {
        if (selectedDirectory) {
          if (failed === 0) {
            alert(
              `✅ Bulk download completed successfully!\n\n📁 ${completed} forms saved to: ${selectedDirectory.name}\n\n💡 Modern browser detected - you chose the save location!`
            );
          } else {
            alert(
              `⚠️ Bulk download completed with some issues!\n\n📁 ${completed} forms saved to: ${selectedDirectory.name}\n❌ ${failed} forms failed to save\n\n💡 Check the browser console for error details.`
            );
          }
        } else {
          if (failed === 0) {
            alert(
              `✅ Download completed successfully!\n\n📁 ${completed} forms downloaded to your default download folder\n\n💡 Tip: Check your browser's download settings to change the default location.\n\n🔧 For "Save As" dialog, try using Chrome/Edge browser.`
            );
          } else {
            alert(
              `⚠️ Download completed with some issues!\n\n📁 ${completed} forms downloaded successfully\n❌ ${failed} forms failed to download\n\n💡 Check the browser console for error details.\n\n🔧 For "Save As" dialog, try using Chrome/Edge browser.`
            );
          }
        }
        setDownloadProgress(0);
      }, 500);
    } catch (error) {
      console.error("Bulk download error:", error);
      alert(
        "❌ Some forms failed to download. Please check the console for details."
      );
    } finally {
      setBulkDownloading(false);
      setDownloadProgress(0);
    }
  };

  // Handle confirmed bulk download after folder selection
  const handleConfirmedBulkDownload = async () => {
    if (!selectedDirectory) return;

    try {
      setBulkDownloading(true);
      setDownloadProgress(0);

      // Get the forms that were originally selected for bulk download
      // This preserves the context from when the folder picker was opened
      let formsToDownload: UserForm[] = [];

      if (pendingDownloadCount === userForms.length) {
        // This is a "Download All" operation - download all forms
        formsToDownload = userForms;
        console.log("🔍 Debug: Download All operation detected");
      } else if (
        pendingDownloadCount > 0 &&
        pendingDownloadCount < userForms.length
      ) {
        // This is a bulk download operation - download only selected forms
        formsToDownload = userForms.filter((form) =>
          selectedFormsForDownload.includes(form.id)
        );
        console.log("🔍 Debug: Bulk download operation detected");
      } else {
        // Fallback - should not happen
        formsToDownload = userForms;
        console.log("🔍 Debug: Fallback to all forms");
      }

      // Debug logging to see what's happening
      console.log("🔍 Debug: Bulk download context:", {
        pendingDownloadCount,
        userFormsCount: userForms.length,
        selectedFormsForDownload: selectedFormsForDownload,
        formsToDownloadCount: formsToDownload.length,
        formsToDownload: formsToDownload.map((f) => f.form_name),
        isDownloadAll: pendingDownloadCount === userForms.length,
        isBulkDownload:
          pendingDownloadCount > 0 && pendingDownloadCount < userForms.length,
      });

      if (formsToDownload.length === 0) {
        alert("No forms selected for download.");
        return;
      }

      let completed = 0;
      let failed = 0;

      for (let index = 0; index < formsToDownload.length; index++) {
        const form = formsToDownload[index];

        try {
          // Update progress
          setDownloadProgress(
            Math.round((index / formsToDownload.length) * 100)
          );

          const response = await apiClient.downloadForm(form.id);

          if (response.pdfUrl) {
            // Create enhanced filename
            const timestamp = new Date().toISOString().slice(0, 10);
            const caseInfo = cases.find((c) => c.id === selectedCase);
            const clientName = caseInfo?.clientName || "client";
            const cleanClientName = clientName.replace(/[^a-zA-Z0-9]/g, "_");

            const enhancedFileName = `${cleanClientName}_${form.form_name.replace(
              ".pdf",
              ""
            )}_${timestamp}.pdf`;

            // Save to selected folder using File System Access API
            try {
              const fileHandle = await selectedDirectory.getFileHandle(
                enhancedFileName,
                { create: true }
              );
              const writable = await fileHandle.createWritable();

              // Fetch the file content
              const fileResponse = await fetch(response.pdfUrl);
              const blob = await fileResponse.blob();

              // Write to selected folder
              await writable.write(blob);
              await writable.close();

              completed++;
              console.log(`✅ Saved ${enhancedFileName} to selected folder`);
            } catch (writeError) {
              console.error(
                `Error writing ${enhancedFileName} to selected folder:`,
                writeError
              );
              failed++;
            }

            // Small delay between downloads to prevent browser overload
            if (index < formsToDownload.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          }
        } catch (error) {
          console.error(`Error downloading ${form.form_name}:`, error);
          failed++;
          // Continue with other forms
        }
      }

      // Final progress update
      setDownloadProgress(100);

      // Show completion message
      setTimeout(() => {
        if (failed === 0) {
          alert(
            `✅ Bulk download completed successfully!\n\n📁 ${completed} forms saved to: ${selectedDirectory.name}\n\n💡 Modern browser detected - you chose the save location!`
          );
        } else {
          alert(
            `⚠️ Bulk download completed with some issues!\n\n📁 ${completed} forms saved to: ${selectedDirectory.name}\n❌ ${failed} forms failed to save\n\n💡 Check the browser console for error details.`
          );
        }
        setDownloadProgress(0);
        setSelectedDirectory(null);
        setPendingDownloadCount(0);

        // Clear selected forms for bulk download if that was the context
        if (
          pendingDownloadCount > 0 &&
          pendingDownloadCount < userForms.length
        ) {
          setSelectedFormsForDownload([]);
        }
      }, 500);
    } catch (error) {
      console.error("Confirmed bulk download error:", error);
      alert(
        "❌ Some forms failed to download. Please check the console for details."
      );
    } finally {
      setBulkDownloading(false);
      setDownloadProgress(0);
      setSelectedDirectory(null);
      setPendingDownloadCount(0);
    }
  };

  // Get the effective status for display (prioritize auto_fill_status if available)
  const getEffectiveStatus = (form: UserForm): string => {
    // If auto_fill_status is available and not 'not_started', use it
    if (form.auto_fill_status && form.auto_fill_status !== "not_started") {
      return form.auto_fill_status;
    }
    // Otherwise fall back to the regular status
    return form.status;
  };

  // Get a more descriptive status for display
  const getDisplayStatus = (form: UserForm): string => {
    const effectiveStatus = getEffectiveStatus(form);

    // For unsupported autofill forms, show more descriptive status
    if (!isFormAutofillSupported(form.form_name)) {
      if (effectiveStatus === "not_started") {
        return "Ready to Download";
      }
      if (effectiveStatus === "draft") {
        return "Ready to Download";
      }
    }

    return effectiveStatus;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: "!bg-gray-300", text: "Draft" },
      not_started: { color: "!bg-gray-300", text: "Not Started" },
      queued: { color: "!bg-amber-300", text: "Queued" },
      processing: { color: "!bg-sky-300", text: "Processing" },
      filled: { color: "!bg-emerald-300", text: "Auto-Filled" }, // ✅ Soft emerald green
      failed: { color: "!bg-rose-300", text: "Failed" }, // ✅ Soft rose red
      reviewing: { color: "!bg-amber-300", text: "Under Review" },
      ready: { color: "!bg-emerald-300", text: "Ready" },
      completed: { color: "!bg-violet-300", text: "Completed" },
      "Ready to Download": { color: "!bg-sky-300", text: "Ready to Download" },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
        style={{
          backgroundColor:
            status === "failed"
              ? "#fecaca"
              : status === "filled"
              ? "#dcfce7"
              : status === "queued"
              ? "#fef3c7"
              : status === "processing"
              ? "#e0f2fe"
              : status === "draft"
              ? "#f3f4f6"
              : status === "not_started"
              ? "#f3f4f6"
              : status === "reviewing"
              ? "#fef3c7"
              : status === "ready"
              ? "#dcfce7"
              : status === "completed"
              ? "#f3e8ff"
              : "#e0f2fe", // default sky blue
          color:
            status === "failed"
              ? "#dc2626"
              : status === "filled"
              ? "#16a34a"
              : status === "queued"
              ? "#d97706"
              : status === "processing"
              ? "#0284c7"
              : status === "draft"
              ? "#6b7280"
              : status === "not_started"
              ? "#6b7280"
              : status === "reviewing"
              ? "#d97706"
              : status === "ready"
              ? "#16a34a"
              : status === "completed"
              ? "#7c3aed"
              : "#0284c7", // default sky blue
          fontWeight: "600",
          fontSize: "11px",
          padding: "6px 12px",
          borderRadius: "12px",
          border:
            status === "failed"
              ? "1px solid #fca5a5"
              : status === "filled"
              ? "1px solid #bbf7d0"
              : status === "queued"
              ? "1px solid #fde68a"
              : status === "processing"
              ? "1px solid #7dd3fc"
              : status === "draft"
              ? "1px solid #d1d5db"
              : status === "not_started"
              ? "1px solid #d1d5db"
              : status === "reviewing"
              ? "1px solid #fde68a"
              : status === "ready"
              ? "1px solid #bbf7d0"
              : status === "completed"
              ? "1px solid #c4b5fd"
              : "1px solid #7dd3fc", // default sky blue
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          transition: "all 0.3s ease",
          cursor: "default",
          display: "inline-block",
          minWidth: "80px",
          textAlign: "center",
          letterSpacing: "0.025em",
        }}
      >
        {config.text}
      </span>
    );
  };

  const getStatusClass = (status: string) => {
    const statusConfig = {
      draft: "status-pending-review",
      not_started: "status-pending-review",
      queued: "status-pending-review",
      processing: "status-pending-review",
      filled: "status-approved", // ✅ Green for success
      failed: "status-rejected", // ✅ Red for failure
      reviewing: "status-pending-review",
      ready: "status-approved",
      completed: "status-approved",
    };

    return (
      statusConfig[status as keyof typeof statusConfig] ||
      "status-pending-review"
    );
  };

  const getStatusText = (status: string) => {
    const statusConfig = {
      draft: "Draft",
      not_started: "Not Started",
      queued: "Queued",
      processing: "Processing",
      filled: "Auto-Filled",
      failed: "Failed",
      reviewing: "Under Review",
      ready: "Ready",
      completed: "Completed",
    };

    return statusConfig[status as keyof typeof statusConfig] || "Draft";
  };

  // Check if form supports autofill
  const isFormAutofillSupported = (formName: string): boolean => {
    // Forms that support autofill
    const autofillSupportedForms = [
      // Visitor Visa forms
      "imm5257e.pdf",
      "imm5707e.pdf",
      "imm5475e.pdf", 
      "imm5476e.pdf",
      // Work Permit forms
      "imm1295e.pdf",
      // Note: imm5707e, imm5475e, imm5476e are shared between case types
    ];
    return autofillSupportedForms.includes(formName);
  };

  // Get form description
  const getFormDescription = (formName: string): string => {
    const descriptions: { [key: string]: string } = {
      "imm5257e.pdf": "Application for Temporary Resident Visa",
      "imm5707e.pdf": "Family Information",
      "imm5475e.pdf": "Authority to release personal info",
      "imm5476e.pdf": "Use of a representative",
      "imm5645e.pdf": "Family Information",
      "imm5721e.pdf":
        "Document Checklist Temporary Resident Visa (In Canada Applicant)",
      "imm5484e.pdf": "Document Checklist For a Temporary Resident Visa",
      "imm5558e.pdf": "Document Checklist Visitor (In Canada Applicant)",
      "imm5708e.pdf":
        "Application to Change Conditions Extend my Stay or Remain in Canada as a Visitor/Resident",
    };
    return descriptions[formName] || "Form";
  };

  // Check if a form can be deleted based on its status
  const canFormBeDeleted = (form: UserForm): boolean => {
    // Forms can only be deleted if they are:
    // 1. 'not_started' - newly added forms that haven't been processed
    // 2. 'filled' - successfully auto-filled forms
    // 3. 'failed' - failed autofill attempts
    // 4. 'ready' - forms that are ready but not processing
    // 5. 'completed' - completed forms
    // 6. 'reviewing' - forms under review
    // 7. 'draft' - draft forms

    // CANNOT delete if currently being processed
    const processingStatuses = ["queued", "processing"];
    const currentStatus = form.auto_fill_status || form.status;

    return !processingStatuses.includes(currentStatus);
  };

  // Check if a form can be auto-filled based on its status
  const canFormBeAutofilled = (form: UserForm): boolean => {
    // A form can be auto-filled if:
    // 1. It supports autofill
    // 2. It's not currently being processed (queued/processing)
    // 3. It can be autofilled regardless of current status

    if (!isFormAutofillSupported(form.form_name)) {
      return false;
    }

    // Don't allow autofill if currently processing
    if (form.status === "queued" || form.status === "processing") {
      return false;
    }

    // Allow autofill for ALL other statuses:
    // - 'not_started' (new forms)
    // - 'draft' (draft forms)
    // - 'failed' (failed autofills - can retry)
    // - 'filled' (completed forms - can refill with new data)
    // - 'ready' (ready forms - can refill)
    // - 'reviewing' (forms under review - can refill)
    // - 'completed' (completed forms - can refill)
    // This enables users to refill forms when client information is updated
    return true;
  };

  return (
    <div className="p-6">
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          /* Enhanced status badge styles with ultra-light colors */
          .status-badge {
            transition: all 0.3s ease;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
            border-radius: 12px;
            padding: 6px 12px;
            font-size: 11px;
            letter-spacing: 0.025em;
          }
          
          .status-badge:hover {
            transform: translateY(-1px);
            box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
          }
          
          /* Ultra-light, pleasant status colors */
          .status-failed {
            background: linear-gradient(135deg, #fecaca, #fca5a5);
            border: 1px solid #fca5a5;
            color: #dc2626;
          }
          
          .status-success {
            background: linear-gradient(135deg, #dcfce7, #bbf7d0);
            border: 1px solid #bbf7d0;
            color: #16a34a;
          }
          
          .status-queued {
            background: linear-gradient(135deg, #fef3c7, #fde68a);
            border: 1px solid #fde68a;
            color: #d97706;
          }
          
          .status-processing {
            background: linear-gradient(135deg, #e0f2fe, #7dd3fc);
            border: 1px solid #7dd3fc;
            color: #0284c7;
          }
          
          .status-draft {
            background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
            border: 1px solid #d1d5db;
            color: #6b7280;
          }
        `}
      </style>

      <div
        className="documents-header"
        style={{
          background:
            "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 50%, rgba(147, 51, 234, 0.1) 100%)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(139, 92, 246, 0.2)",
          padding: "20px",
          borderRadius: "16px",
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h1
          style={{
            fontSize: "28px",
            margin: "0",
            color: "#000000",
            fontWeight: "700",
          }}
        >
          Case Forms
        </h1>

        <div className="case-selector">
          <select
            value={selectedCase || ""}
            onChange={(e) => setSelectedCase(e.target.value)}
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(139, 92, 246, 0.3)",
              borderRadius: "12px",
              padding: "12px 16px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              minWidth: "300px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 16px rgba(139, 92, 246, 0.1)",
            }}
          >
            <option value="">Select a case</option>
            {cases.map((case_) => (
              <option key={case_.id} value={case_.id}>
                {case_.caseId} - {case_.clientName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedCase ? (
        <div
          style={{
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(139, 92, 246, 0.2)",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 8px 32px rgba(139, 92, 246, 0.1)",
          }}
        >
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Case Selected
            </h3>
            <p className="text-gray-500">
              Please select a case from the dropdown above to view and manage
              its forms.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Recommended Forms Section */}
          <div
            className="required-documents-panel"
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid transparent",
              borderRadius: "16px",
              marginBottom: "24px",
              boxShadow: "none",
            }}
          >
            <div
              className="panel-header"
              style={{
                padding: "20px 24px",
                marginBottom: "16px",
                borderBottom: "1px solid transparent",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  margin: "0 0 8px 0",
                  color: "#000000",
                  fontWeight: "600",
                }}
              >
                Recommended Forms
              </h2>
              <p
                style={{
                  fontSize: "14px",
                  margin: "0 0 16px 0",
                  color: "#6b7280",
                }}
              >
                Select forms to add to your case.
              </p>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <button
                  onClick={() => {
                    if (selectedCase) {
                      loadUserForms(selectedCase, true);
                    }
                  }}
                  className="refresh-btn"
                  title="Refresh form status"
                  style={{
                    fontSize: "12px",
                    padding: "8px 16px",
                    background:
                      "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                    borderRadius: "8px",
                    color: "#8b5cf6",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(139, 92, 246, 0.1)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 16px rgba(139, 92, 246, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(139, 92, 246, 0.1)";
                  }}
                >
                  Refresh Status
                </button>



                <button
                  onClick={() => setShowCustomFormSearch(true)}
                  className="add-category-btn"
                  title="Search custom forms"
                  style={{
                    fontSize: "12px",
                    padding: "8px 16px",
                    background:
                      "linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    borderRadius: "8px",
                    color: "#22c55e",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(34, 197, 94, 0.1)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 16px rgba(34, 197, 94, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(34, 197, 94, 0.1)";
                  }}
                >
                  Search Custom Forms
                </button>
              </div>
            </div>

            {recommendedForms.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-4">📋</div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  No Forms Available
                </h4>
                <p className="text-gray-500 mb-4">
                  No recommended forms found for {caseData?.caseType} (
                  {caseData?.country}).
                </p>
                <div className="text-sm text-gray-400 bg-gray-50 p-3 rounded">
                  <p>
                    <strong>Debug Info:</strong>
                  </p>
                  <p>Case Type ID: {caseData?.caseTypeId}</p>
                  <p>Country: {caseData?.country}</p>
                  <p>Forms Found: {recommendedForms.length}</p>
                </div>
              </div>
            ) : (
              <div className="cases-table">
                <table>
                  <thead>
                    <tr>
                      <th>FORM NAME</th>
                      <th>DESCRIPTION</th>
                      <th>TYPE</th>
                      <th>AUTOFILL</th>
                      <th>SELECT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recommendedForms
                      .filter((form) => !addedFormIds.includes(form.id))
                      .map((form) => (
                        <tr key={form.id}>
                          <td>
                            <div className="client-info">
                              <div className="client-avatar">•</div>
                              <div>
                                <div className="client-name">{form.name}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div
                              className="client-email"
                              style={{ fontSize: "0.75rem", color: "#6b7280" }}
                            >
                              {form.description}
                            </div>
                          </td>
                          <td>
                            <span
                              className="priority-badge"
                              style={{ fontSize: "0.7rem" }}
                            >
                              {form.form_type.replace("_", " ")}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`autofill-badge ${
                                form.autofill_supported
                                  ? "supported"
                                  : "not-supported"
                              }`}
                              style={{
                                fontSize: "0.7rem",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                backgroundColor: form.autofill_supported
                                  ? "#dcfce7"
                                  : "#fef2f2",
                                color: form.autofill_supported
                                  ? "#166534"
                                  : "#991b1b",
                                border: `1px solid ${
                                  form.autofill_supported
                                    ? "#bbf7d0"
                                    : "#fecaca"
                                }`,
                              }}
                            >
                              {form.autofill_supported
                                ? "Supported"
                                : "Download Only"}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <input
                                type="checkbox"
                                checked={selectedForms.includes(form.id)}
                                onChange={() => handleFormSelection(form.id)}
                                className="form-checkbox"
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  cursor: "pointer",
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                {selectedForms.length > 0 && (
                  <div
                    className="category-selection"
                    style={{ marginTop: "16px", textAlign: "center" }}
                  >
                    <div className="category-selector">
                      <button
                        onClick={handleAddForms}
                        disabled={loading}
                        className="add-category-btn"
                        style={{
                          fontSize: "14px",
                          padding: "12px 24px",
                          background: loading
                            ? "rgba(107, 114, 128, 0.1)"
                            : "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)",
                          backdropFilter: "blur(10px)",
                          border: `1px solid ${
                            loading
                              ? "rgba(107, 114, 128, 0.3)"
                              : "rgba(139, 92, 246, 0.3)"
                          }`,
                          borderRadius: "12px",
                          color: loading ? "#6b7280" : "#8b5cf6",
                          fontWeight: "600",
                          cursor: loading ? "not-allowed" : "pointer",
                          transition: "all 0.3s ease",
                          boxShadow: loading
                            ? "none"
                            : "0 4px 16px rgba(139, 92, 246, 0.2)",
                          opacity: loading ? 0.7 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 24px rgba(139, 92, 246, 0.3)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 16px rgba(139, 92, 246, 0.2)";
                          }
                        }}
                      >
                        {loading
                          ? "Adding Forms..."
                          : `Add ${selectedForms.length} Form(s)`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Forms Section */}
          <div
            className="uploaded-documents-panel"
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid transparent",
              borderRadius: "16px",
              boxShadow: "none",
            }}
          >
            <div
              className="panel-header"
              style={{
                padding: "20px 24px",
                marginBottom: "16px",
                borderBottom: "1px solid transparent",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  margin: "0 0 8px 0",
                  color: "#000000",
                  fontWeight: "600",
                }}
              >
                Selected Forms
              </h2>
              <div
                className="panel-controls"
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                {/* Bulk Delete Button */}
                {selectedFormsForDelete.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="delete-documents-btn"
                    title={`Delete ${
                      selectedFormsForDelete.length
                    } selected form${
                      selectedFormsForDelete.length > 1 ? "s" : ""
                    }`}
                    style={{
                      fontSize: "12px",
                      padding: "8px 16px",
                      background: bulkDeleting
                        ? "rgba(107, 114, 128, 0.1)"
                        : "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${
                        bulkDeleting
                          ? "rgba(107, 114, 128, 0.3)"
                          : "rgba(239, 68, 68, 0.3)"
                      }`,
                      borderRadius: "8px",
                      color: bulkDeleting ? "#6b7280" : "#ef4444",
                      fontWeight: "500",
                      cursor: bulkDeleting ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: bulkDeleting
                        ? "none"
                        : "0 2px 8px rgba(239, 68, 68, 0.1)",
                      opacity: bulkDeleting ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!bulkDeleting) {
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(239, 68, 68, 0.2)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!bulkDeleting) {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(239, 68, 68, 0.1)";
                      }
                    }}
                  >
                    {bulkDeleting
                      ? "Deleting..."
                      : `Delete (${selectedFormsForDelete.length})`}
                  </button>
                )}

                {/* Bulk Download Button */}
                {selectedFormsForDownload.length > 0 && (
                  <button
                    onClick={handleBulkDownload}
                    disabled={bulkDownloading}
                    className="download-documents-btn"
                    title={`Download ${
                      selectedFormsForDownload.length
                    } selected form${
                      selectedFormsForDownload.length > 1 ? "s" : ""
                    }`}
                    style={{
                      fontSize: "12px",
                      padding: "8px 16px",
                      background: bulkDownloading
                        ? "rgba(107, 114, 128, 0.1)"
                        : "linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${
                        bulkDownloading
                          ? "rgba(107, 114, 128, 0.3)"
                          : "rgba(34, 197, 94, 0.3)"
                      }`,
                      borderRadius: "8px",
                      color: bulkDownloading ? "#6b7280" : "#22c55e",
                      fontWeight: "500",
                      cursor: bulkDownloading ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: bulkDownloading
                        ? "none"
                        : "0 2px 8px rgba(34, 197, 94, 0.1)",
                      opacity: bulkDownloading ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!bulkDownloading) {
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(34, 197, 94, 0.2)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!bulkDownloading) {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(34, 197, 94, 0.1)";
                      }
                    }}
                  >
                    {bulkDownloading
                      ? "Downloading..."
                      : `Download (${selectedFormsForDownload.length})`}
                  </button>
                )}

                {/* Bulk Auto-Fill Button */}
                {selectedFormsForAutoFill.length > 0 && (
                  <button
                    onClick={handleBulkAutoFill}
                    disabled={bulkAutoFilling}
                    className="autofill-documents-btn"
                    title={`Auto-fill ${
                      selectedFormsForAutoFill.length
                    } selected form${
                      selectedFormsForAutoFill.length > 1 ? "s" : ""
                    }`}
                    style={{
                      fontSize: "12px",
                      padding: "8px 16px",
                      background: bulkAutoFilling
                        ? "rgba(107, 114, 128, 0.1)"
                        : "linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${
                        bulkAutoFilling
                          ? "rgba(107, 114, 128, 0.3)"
                          : "rgba(124, 58, 237, 0.3)"
                      }`,
                      borderRadius: "8px",
                      color: bulkAutoFilling ? "#6b7280" : "#7c3aed",
                      fontWeight: "500",
                      cursor: bulkAutoFilling ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: bulkAutoFilling
                        ? "none"
                        : "0 2px 8px rgba(124, 58, 237, 0.1)",
                      opacity: bulkAutoFilling ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!bulkAutoFilling) {
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(124, 58, 237, 0.2)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!bulkAutoFilling) {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(124, 58, 237, 0.1)";
                      }
                    }}
                  >
                    {bulkAutoFilling
                      ? "Auto-filling..."
                      : `Auto-fill (${selectedFormsForAutoFill.length})`}
                  </button>
                )}

                {/* Download All Button */}
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <button
                    onClick={handleDownloadAll}
                    className="download-all-btn"
                    disabled={userForms.length === 0}
                    style={{
                      background:
                        userForms.length === 0
                          ? "rgba(107, 114, 128, 0.1)"
                          : "linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)",
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${
                        userForms.length === 0
                          ? "rgba(107, 114, 128, 0.3)"
                          : "rgba(34, 197, 94, 0.3)"
                      }`,
                      borderRadius: "12px",
                      color: userForms.length === 0 ? "#6b7280" : "#22c55e",
                      cursor:
                        userForms.length === 0 ? "not-allowed" : "pointer",
                      opacity: userForms.length === 0 ? 0.7 : 1,
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "12px 24px",
                      transition: "all 0.3s ease",
                      boxShadow:
                        userForms.length === 0
                          ? "none"
                          : "0 4px 16px rgba(34, 197, 94, 0.1)",
                    }}
                    onMouseEnter={(e) => {
                      if (userForms.length > 0) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 8px 24px rgba(34, 197, 94, 0.2)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (userForms.length > 0) {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(34, 197, 94, 0.1)";
                      }
                    }}
                  >
                    Download All
                  </button>

                  {/* Tooltip Icon */}
                  <div
                    className="tooltip-container"
                    style={{ position: "relative" }}
                    ref={tooltipRef}
                  >
                    <button
                      className="tooltip-trigger"
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "16px",
                        color: "#6b7280",
                        cursor: "pointer",
                        padding: "4px",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() =>
                        setShowDownloadTooltip(!showDownloadTooltip)
                      }
                      title="Download folder selection help"
                    >
                      ?
                    </button>
                  </div>
                </div>

                {/* Download Progress Indicator */}
                {downloadProgress > 0 && downloadProgress < 100 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "12px",
                      color: "#6b7280",
                    }}
                  >
                    <div
                      style={{
                        width: "60px",
                        height: "4px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "2px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${downloadProgress}%`,
                          height: "100%",
                          backgroundColor: "#059669",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    <span>{downloadProgress}%</span>
                  </div>
                )}
              </div>

              {/* Download Info Box - REMOVED */}
              {/* No longer needed as the interface is now clean and intuitive */}
            </div>

            {userForms.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📄</div>
                <h3>No forms added yet</h3>
                <p>Select recommended forms above to get started.</p>
              </div>
            ) : (
              <div className="cases-table">
                <table>
                  <thead>
                    <tr>
                      <th>FORM NAME</th>
                      <th>DESC</th>
                      <th>STATUS</th>
                      <th>CREATED</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userForms.map((form) => (
                      <tr
                        key={form.id}
                        style={{
                          backgroundColor: recentlyUpdatedForms.has(form.id)
                            ? "#fef3c7"
                            : undefined,
                          transition: "background-color 0.5s ease",
                          border: recentlyUpdatedForms.has(form.id)
                            ? "2px solid #f59e0b"
                            : undefined,
                        }}
                      >
                        <td>
                          <div className="client-info">
                            <div
                              className="client-avatar"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "8px",
                                background:
                                  "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)",
                                border: "1px solid rgba(139, 92, 246, 0.2)",
                              }}
                            >
                              {React.createElement(FiFile as any, {
                                style: { color: "#8b5cf6", fontSize: "16px" },
                              })}
                            </div>
                            <div>
                              <div className="client-name">
                                {form.form_name}
                              </div>
                              <div
                                className="form-type-indicator"
                                style={{
                                  fontSize: "10px",
                                  color: isFormAutofillSupported(form.form_name)
                                    ? "#7c3aed"
                                    : "#9ca3af",
                                  fontStyle: "italic",
                                }}
                              >
                                {isFormAutofillSupported(form.form_name)
                                  ? "Autofill Supported"
                                  : "Download Only"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            className="client-email"
                            style={{ fontSize: "0.75rem", color: "#6b7280" }}
                          >
                            {getFormDescription(form.form_name)}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {getStatusBadge(getDisplayStatus(form))}
                        </td>
                        <td>
                          {new Date(form.created_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "2-digit",
                              year: "numeric",
                            }
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            {/* Checkbox for bulk deletion */}
                            <div
                              className="delete-checkbox-container"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedFormsForDelete.includes(
                                  form.id
                                )}
                                onChange={() =>
                                  handleBulkFormSelection(form.id, "delete")
                                }
                                className="delete-checkbox"
                                disabled={!canFormBeDeleted(form)}
                                title={
                                  canFormBeDeleted(form)
                                    ? "Select this form for deletion"
                                    : `Cannot delete while ${
                                        form.auto_fill_status || form.status
                                      } - wait for completion`
                                }
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  accentColor: "#dc2626",
                                  cursor: canFormBeDeleted(form)
                                    ? "pointer"
                                    : "not-allowed",
                                  opacity: canFormBeDeleted(form) ? 1 : 0.4,
                                }}
                              />
                              <span
                                className="delete-hint"
                                title={
                                  canFormBeDeleted(form)
                                    ? "Select this form for deletion"
                                    : `Cannot delete while ${
                                        form.auto_fill_status || form.status
                                      } - wait for completion`
                                }
                                style={{
                                  fontSize: "12px",
                                  color: "#ef4444",
                                  cursor: "help",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {React.createElement(FiTrash2 as any)}
                              </span>
                            </div>

                            {/* Checkbox for bulk download */}
                            <div
                              className="download-checkbox-container"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedFormsForDownload.includes(
                                  form.id
                                )}
                                onChange={() =>
                                  handleBulkFormSelection(form.id, "download")
                                }
                                className="download-checkbox"
                                title="Select this form for download"
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  accentColor: "#059669",
                                  cursor: "pointer",
                                  opacity: 1,
                                }}
                              />
                              <span
                                className="download-hint"
                                title="Select this form for download"
                                style={{
                                  fontSize: "12px",
                                  color: "#22c55e",
                                  cursor: "help",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {React.createElement(FiDownload as any)}
                              </span>
                            </div>

                            {/* Checkbox for bulk auto-fill (for all supported forms regardless of status) */}
                            {isFormAutofillSupported(form.form_name) &&
                              canFormBeAutofilled(form) && (
                                <div
                                  className="autofill-checkbox-container"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedFormsForAutoFill.includes(
                                      form.id
                                    )}
                                    onChange={() =>
                                      handleBulkFormSelection(
                                        form.id,
                                        "autofill"
                                      )
                                    }
                                    className="autofill-checkbox"
                                    title={`Select ${
                                      form.form_name
                                    } for auto-fill (${
                                      form.status === "failed"
                                        ? "retry"
                                        : form.status === "filled"
                                        ? "refill"
                                        : "autofill"
                                    })`}
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      accentColor: "#7c3aed",
                                      cursor: "pointer",
                                    }}
                                  />
                                  <span
                                    className="autofill-hint"
                                    title={`Select ${
                                      form.form_name
                                    } for auto-fill (${
                                      form.status === "failed"
                                        ? "retry"
                                        : form.status === "filled"
                                        ? "refill"
                                        : "autofill"
                                    })`}
                                    style={{
                                      fontSize: "10px",
                                      color: "#7c3aed",
                                      cursor: "help",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {React.createElement(FiZap as any)}
                                  </span>
                                </div>
                              )}

                            {/* Show download-only indicator for unsupported forms (regardless of status) */}
                            {!isFormAutofillSupported(form.form_name) && (
                              <span
                                className="download-only-hint"
                                title={`${form.form_name} can only be downloaded, not auto-filled`}
                                style={{
                                  fontSize: "10px",
                                  color: "#9ca3af",
                                  cursor: "help",
                                  marginLeft: "4px",
                                }}
                              >
                                {React.createElement(FiDownload as any)}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Custom Form Search Modal */}
      {showCustomFormSearch && (
        <div 
          className="modal-overlay"
          onClick={() => setShowCustomFormSearch(false)}
        >
          <div 
            className="modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>🔍 Search Custom Forms</h3>
              <button
                onClick={() => setShowCustomFormSearch(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Search Forms:</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="text"
                    value={customFormSearch}
                    onChange={(e) => setCustomFormSearch(e.target.value)}
                    placeholder="Enter form name or description..."
                    style={{ flex: 1 }}
                  />
                  <button
                    onClick={handleCustomFormSearch}
                    className="add-btn"
                    style={{ fontSize: "12px", padding: "6px 12px" }}
                  >
                    🔍 Search
                  </button>
                </div>
              </div>

              {customFormResults.length > 0 && (
                <div className="cases-table" style={{ marginTop: "16px" }}>
                  <table>
                    <thead>
                      <tr>
                        <th>FORM NAME</th>
                        <th>DESCRIPTION</th>
                        <th>TYPE</th>
                        <th>SELECT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customFormResults.map((form) => (
                        <tr key={form.id}>
                          <td>
                            <div className="client-info">
                              <div className="client-avatar">📄</div>
                              <div>
                                <div className="client-name">{form.name}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div
                              className="client-email"
                              style={{ fontSize: "0.75rem", color: "#6b7280" }}
                            >
                              {form.description}
                            </div>
                          </td>
                          <td>
                            <span
                              className="priority-badge"
                              style={{ fontSize: "0.7rem" }}
                            >
                              {form.form_type.replace("_", " ")}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <input
                                type="checkbox"
                                checked={selectedForms.includes(form.id)}
                                onChange={() => handleFormSelection(form.id)}
                                className="form-checkbox"
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  cursor: "pointer",
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {customFormResults.length === 0 && customFormSearch && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#6b7280",
                  }}
                >
                  No forms found matching your search.
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowCustomFormSearch(false)}
                className="cancel-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Folder Confirmation Modal */}
      {showFolderConfirm && selectedDirectory && (
        <div 
          className="modal-overlay"
          onClick={() => {
            setShowFolderConfirm(false);
            setSelectedDirectory(null);
            setPendingDownloadCount(0);
          }}
        >
          <div 
            className="modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>📁 Confirm Folder Selection</h3>
              <button
                onClick={() => {
                  setShowFolderConfirm(false);
                  setSelectedDirectory(null);
                  setPendingDownloadCount(0);
                }}
                className="close-btn"
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>📁</div>
                <h4 style={{ margin: "0 0 16px 0", color: "#1f2937" }}>
                  Download to Selected Folder
                </h4>
                <p
                  style={{
                    margin: "0 0 20px 0",
                    color: "#6b7280",
                    fontSize: "14px",
                  }}
                >
                  You've selected: <strong>{selectedDirectory.name}</strong>
                </p>
                <p
                  style={{
                    margin: "0 0 20px 0",
                    color: "#6b7280",
                    fontSize: "14px",
                  }}
                >
                  This will save <strong>{pendingDownloadCount} forms</strong>{" "}
                  to this folder.
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={() => {
                      setShowFolderConfirm(false);
                      setSelectedDirectory(null);
                      setPendingDownloadCount(0);
                    }}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#6b7280",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowFolderConfirm(false);
                      // Start the actual download process
                      handleConfirmedBulkDownload();
                    }}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#059669",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    Continue Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modern Bulk Delete Confirmation Modal */}
      <FormDeleteConfirmModal
        isOpen={bulkDeleteModal}
        formsToDelete={userForms.filter((form) =>
          selectedFormsForDelete.includes(form.id)
        )}
        isDeleting={bulkDeleting}
        onConfirm={confirmBulkDelete}
        onCancel={cancelBulkDelete}
        getStatusText={getStatusText}
      />

      {/* Delete Confirmation Modal */}
      {deletingForm && (
        <div 
          className="modal-overlay"
          onClick={cancelDeleteForm}
        >
          <div 
            className="modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>⚠️ Delete Form</h3>
              <button onClick={cancelDeleteForm} className="close-btn">
                ✕
              </button>
            </div>
            <div className="modal-content">
              <p>
                Are you sure you want to delete{" "}
                <strong>{deletingForm.name}</strong>?
              </p>
              <p
                style={{ color: "#dc2626", fontSize: "14px", marginTop: "8px" }}
              >
                ⚠️ This action is irreversible and will permanently delete all
                form data including:
              </p>
              <ul
                style={{
                  color: "#dc2626",
                  fontSize: "14px",
                  marginTop: "4px",
                  marginLeft: "20px",
                }}
              >
                <li>Filled form data</li>
                <li>Auto-filled information</li>
                <li>Manual edits and changes</li>
                <li>Review notes and comments</li>
              </ul>
              <p
                style={{ color: "#6b7280", fontSize: "12px", marginTop: "8px" }}
              >
                Current status:{" "}
                <strong>{getStatusText(deletingForm.status)}</strong>
              </p>
            </div>
            <div className="modal-actions">
              <button onClick={cancelDeleteForm} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={confirmDeleteForm}
                className="delete-confirm-btn"
                style={{
                  backgroundColor: "#dc2626",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Folder Selection Help Modal */}
      {showDownloadTooltip && (
        <div 
          className="modal-overlay"
          onClick={() => setShowDownloadTooltip(false)}
        >
          <div 
            className="modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>📁 How to Select Download Folder</h3>
              <button
                onClick={() => setShowDownloadTooltip(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <div style={{ marginBottom: "20px" }}>
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#6b7280",
                  }}
                >
                  <span style={{ color: "#dc2626" }}>🎯</span>{" "}
                  <strong>Step-by-Step Instructions:</strong>
                </p>
                <ol
                  style={{
                    margin: "0 0 0 20px",
                    padding: "0",
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}
                >
                  <li style={{ marginBottom: "8px" }}>
                    Click the download button (Download All or Download
                    Selected)
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    Choose your folder location:
                    <ul style={{ margin: "8px 0 0 20px", padding: "0" }}>
                      <li style={{ marginBottom: "4px" }}>
                        Navigate to Documents or Downloads
                      </li>
                      <li style={{ marginBottom: "4px" }}>
                        <strong>
                          Important: Select a subfolder inside
                          Documents/Downloads
                        </strong>
                      </li>
                      <li style={{ marginBottom: "4px" }}>
                        Do NOT select the root Documents or Downloads folder
                      </li>
                    </ul>
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    Click "Select" on the subfolder
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    Grant permission: Click "Edit files" when browser asks
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    Forms will download to your selected subfolder
                  </li>
                </ol>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#6b7280",
                  }}
                >
                  <span style={{ color: "#16a34a" }}>✅</span>{" "}
                  <strong>Good Folder Choices:</strong>
                </p>
                <ul
                  style={{
                    margin: "0 0 0 20px",
                    padding: "0",
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}
                >
                  <li style={{ marginBottom: "6px" }}>
                    Documents → Immigration Forms
                  </li>
                  <li style={{ marginBottom: "6px" }}>
                    Documents → Work → Visa Applications
                  </li>
                  <li style={{ marginBottom: "6px" }}>Downloads → My Forms</li>
                  <li style={{ marginBottom: "6px" }}>
                    Documents → Personal → Applications
                  </li>
                </ul>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#6b7280",
                  }}
                >
                  <span style={{ color: "#dc2626" }}>❌</span>{" "}
                  <strong>Won't Work (System Folders):</strong>
                </p>
                <ul
                  style={{
                    margin: "0 0 0 20px",
                    padding: "0",
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}
                >
                  <li style={{ marginBottom: "6px" }}>Desktop (root folder)</li>
                  <li style={{ marginBottom: "6px" }}>
                    Documents (root folder)
                  </li>
                  <li style={{ marginBottom: "6px" }}>
                    Downloads (root folder)
                  </li>
                  <li style={{ marginBottom: "6px" }}>Applications folder</li>
                  <li style={{ marginBottom: "6px" }}>System folders</li>
                </ul>
              </div>

              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#fef3c7",
                  borderRadius: "6px",
                  border: "1px solid #f59e0b",
                  marginBottom: "16px",
                }}
              >
                <p style={{ margin: "0", fontSize: "13px", color: "#92400e" }}>
                  <strong>💡 Tip:</strong> Create a new folder inside Documents
                  first, then select it for downloads.
                </p>
              </div>

              <div>
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#6b7280",
                  }}
                >
                  <span style={{ color: "#f59e0b" }}>🔧</span>{" "}
                  <strong>Troubleshooting:</strong>
                </p>
                <ul
                  style={{
                    margin: "0 0 0 20px",
                    padding: "0",
                    fontSize: "13px",
                    lineHeight: "1.6",
                  }}
                >
                  <li style={{ marginBottom: "6px" }}>
                    <strong>"Can't open this folder":</strong> You selected a
                    system folder. Choose a subfolder instead.
                  </li>
                  <li style={{ marginBottom: "6px" }}>
                    <strong>"Permission denied":</strong> Click "Edit files"
                    when the browser asks for permission.
                  </li>
                  <li style={{ marginBottom: "6px" }}>
                    <strong>"Folder not accessible":</strong> Try creating a new
                    folder inside Documents first.
                  </li>
                </ul>
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowDownloadTooltip(false)}
                className="cancel-btn"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Deletion Progress Modal */}
      <FormDeletionProgressModal
        isOpen={formDeletionProgress.isOpen}
        isDeleting={formDeletionProgress.isDeleting}
        totalForms={formDeletionProgress.totalForms}
        deletedCount={formDeletionProgress.deletedCount}
        currentFormName={formDeletionProgress.currentFormName}
        onComplete={() =>
          setFormDeletionProgress((prev) => ({ ...prev, isOpen: false }))
        }
      />
    </div>
  );
}
